import React from 'react';
import { Button } from 'antd';
import { FiFilter, FiRotateCcw } from 'react-icons/fi';
import styles from '@pages/api-portal/styles.module.scss';
import { API_CATALOG_VERSIONS, API_HTTP_METHODS, API_LIFECYCLE_STATUSES } from '@pages/api-portal/constants';
import { CatalogGroupIcon } from '@pages/api-portal/catalog-group-icon';
import { resolveCatalogGroupParam } from '@pages/api-portal/utils';
import { ApiEndpoint, ApiGroup } from '@pages/api-portal/types';

interface CatalogFiltersAsideProps {
    apiGroups: ApiGroup[];
    allEndpoints: ApiEndpoint[];
    selectedGroup: string | null;
    onApplyGroupFilter: (groupParam: string | null) => void;
    selectedMethods: string[];
    onToggleMethod: (value: string) => void;
    selectedVersions: string[];
    onToggleVersion: (value: string) => void;
    selectedStatuses: string[];
    onToggleStatus: (value: string) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
}

/*
 * Left-rail filters for API group, HTTP method, version, and lifecycle status.
 */
export function CatalogFiltersAside({
    apiGroups,
    allEndpoints,
    selectedGroup,
    onApplyGroupFilter,
    selectedMethods,
    onToggleMethod,
    selectedVersions,
    onToggleVersion,
    selectedStatuses,
    onToggleStatus,
    hasActiveFilters,
    onClearFilters,
}: CatalogFiltersAsideProps) {
    return (
        <div style={{ width: 240, borderRight: '1px solid #666a8d', padding: 16, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <FiFilter /> Filters
                </h4>
                {hasActiveFilters && (
                    <Button size="small" onClick={onClearFilters} title="Clear filters">
                        <FiRotateCcw />
                    </Button>
                )}
            </div>

            <div style={{ marginBottom: 20 }}>
                <h5 style={{ color: '#fff', fontSize: 12 }}>API Group</h5>
                {apiGroups.map((group) => {
                    const groupParam = resolveCatalogGroupParam(group, allEndpoints);
                    const isSelected = selectedGroup === groupParam;
                    const endpointCount = allEndpoints.filter((e) => String(e.group).trim() === groupParam).length;
                    return (
                        <div
                            key={String(group.id)}
                            onClick={() => onApplyGroupFilter(isSelected ? null : groupParam)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '6px 10px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                marginBottom: 2,
                                background: isSelected ? 'rgba(252, 173, 1, 0.12)' : undefined,
                                color: isSelected ? '#fcad01' : '#c7c7cd',
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                <CatalogGroupIcon icon={group.icon} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 16 }}>
                                    {group.name}
                                </span>
                            </span>
                            <span className={styles.mutedText} style={{ fontSize: 12 }}>
                                {endpointCount}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div style={{ marginBottom: 20 }}>
                <h5 style={{ color: '#fff', fontSize: 12 }}>HTTP Method</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {API_HTTP_METHODS.map((method) => (
                        <FilterChip
                            key={method}
                            label={method}
                            selected={selectedMethods.includes(method)}
                            onClick={() => onToggleMethod(method)}
                        />
                    ))}
                </div>
            </div>

            <div style={{ marginBottom: 20 }}>
                <h5 style={{ color: '#fff', fontSize: 12 }}>Version</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {API_CATALOG_VERSIONS.map((version) => (
                        <FilterChip
                            key={version}
                            label={version}
                            selected={selectedVersions.includes(version)}
                            onClick={() => onToggleVersion(version)}
                        />
                    ))}
                </div>
            </div>

            <div>
                <h5 style={{ color: '#fff', fontSize: 12 }}>Status</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {API_LIFECYCLE_STATUSES.map((status) => (
                        <FilterChip
                            key={status}
                            label={status}
                            selected={selectedStatuses.includes(status)}
                            onClick={() => onToggleStatus(status)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function FilterChip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <span
            onClick={onClick}
            style={{
                background: selected ? '#4a3300' : '#2e2f45',
                color: selected ? '#fcad01' : '#c7c7cd',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 12,
                cursor: 'pointer',
                textTransform: 'uppercase',
            }}
        >
            {label}
        </span>
    );
}
